import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppModal from "../../Components/Common/AppModal";
import {
  sendFeedback,
  sendFeedbackSuccess,
} from "../../store/feedbackGen/action";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  Stage,
  Layer,
  Line,
  Text,
  Rect,
  Transformer,
  Image as KonvaImage,
} from "react-konva";
import Konva from "konva";
import useImage from "use-image";

import FeedbackRightSideTools from "./feedbackRightSideTools";
import { captureScreenWhenReady } from "../../helpers/captureScreenWhenReady";
import { captureCurrentWindow } from "../../helpers/captureCurrentWindow";
import { canShareScreen } from "../../helpers/captureEnvironment";
import { isDeveloperUser } from "../../helpers/functions/feedbackTool";
import "./feedbackGen.styles.css";

const PresetOffsetNumber = 25;
const BLUR_RADIUS = 16;

// Renders a blurred copy of the underlying image cropped to `rect`, so the
// region is redacted (data hidden) and baked into the exported PNG.
// `scaleX/scaleY` map stage coords -> the image's natural pixel coords for crop.
const BlurRegion = ({ image, rect, scaleX, scaleY }) => {
  const ref = useRef(null);

  // Normalize so dragging up/left still produces a positive-sized rect.
  const x = rect.width < 0 ? rect.x + rect.width : rect.x;
  const y = rect.height < 0 ? rect.y + rect.height : rect.y;
  const w = Math.abs(rect.width);
  const h = Math.abs(rect.height);

  // Filters only take effect on a cached node; re-cache when geometry changes.
  useEffect(() => {
    const node = ref.current;
    if (!node || !image || w < 1 || h < 1) return;
    node.cache();
    node.getLayer()?.batchDraw();
  }, [image, x, y, w, h, scaleX, scaleY]);

  if (!image || w < 1 || h < 1) return null;

  return (
    <KonvaImage
      ref={ref}
      image={image}
      x={x}
      y={y}
      width={w}
      height={h}
      crop={{
        x: x * scaleX,
        y: y * scaleY,
        width: w * scaleX,
        height: h * scaleY,
      }}
      filters={[Konva.Filters.Blur]}
      blurRadius={BLUR_RADIUS}
    />
  );
};

const FeedbackApp = ({ modal, toggle, dataUrl, elementInfo }) => {
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const transformerRef = useRef(null);

  // imageSrc can be from desktop capture OR mobile upload
  const [imageSrc, setImageSrc] = useState(dataUrl || "");
  const [imageBase64] = useImage(imageSrc);

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  const [isDrawing, setIsDrawing] = useState(false);
  const [isAddingText, setIsAddingText] = useState(false);
  const [isAddingRect, setIsAddingRect] = useState(false);
  const [isBlurring, setIsBlurring] = useState(false);

  const [lines, setLines] = useState([]);
  const [texts, setTexts] = useState([]);
  const [rectangles, setRectangles] = useState([]);
  const [blurs, setBlurs] = useState([]);
  const [currentLine, setCurrentLine] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [currentBlur, setCurrentBlur] = useState(null);
  const [selectedShape, setSelectedShape] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#000000");

  const [isMobile, setIsMobile] = useState(false);

  // Developers get "Let AI handle this" ticked by default on a new report, so
  // their bug reports queue straight for AI dev (status 136) unless they opt out.
  // Everyone else never sees the checkbox (Task 24733) and the backend ignores
  // the field for them anyway.
  const isDeveloper = isDeveloperUser();

  const initialFeedbackData = {
    Title: "",
    Description: "",
    IssueType: "",
    AreaAffected: "",
    Severity: "",
    LetAiHandle: isDeveloper,
    Url: "",
    images: [
      {
        Title: "",
        MimeType: "image/png",
        ImageBase64: "",
      },
    ],
  };
  const [feedbackData, setFeedbackData] = useState(initialFeedbackData);

  // Sync passed-in dataUrl (desktop capture) to local state
  useEffect(() => {
    setImageSrc(dataUrl || "");
  }, [dataUrl]);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth < 768);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Mode toggles
  const toggleDrawingMode = () => {
    setIsDrawing((prev) => !prev);
    setIsAddingText(false);
    setIsAddingRect(false);
    setIsBlurring(false);
    setSelectedShape(null);
  };

  const toggleTextMode = () => {
    setIsAddingText((prev) => !prev);
    setIsDrawing(false);
    setIsAddingRect(false);
    setIsBlurring(false);
    setSelectedShape(null);
  };

  const toggleRectMode = () => {
    setIsAddingRect((prev) => !prev);
    setIsDrawing(false);
    setIsAddingText(false);
    setIsBlurring(false);
    setSelectedShape(null);
  };

  const toggleBlurMode = () => {
    setIsBlurring((prev) => !prev);
    setIsDrawing(false);
    setIsAddingText(false);
    setIsAddingRect(false);
    setSelectedShape(null);
  };

  // Shape selection
  const handleSelectShape = (shape) => {
    if (transformerRef.current) {
      setSelectedShape(shape);
      transformerRef.current.nodes([shape]);
      transformerRef.current.getLayer().batchDraw();
    }
  };

  // Pointer handlers (mouse + touch)
  const handlePointerDown = (e) => {
    if (e.target === stageRef.current) {
      setSelectedShape(null);
    }

    if (isDrawing) {
      const pos = e.target.getStage().getPointerPosition();
      setCurrentLine({ points: [pos.x, pos.y] });
    } else if (isAddingText) {
      const pos = e.target.getStage().getPointerPosition();
      const newText = prompt("Enter text:");
      if (newText) {
        setTexts((prev) => [
          ...prev,
          { text: newText, x: pos.x, y: pos.y, fill: selectedColor },
        ]);
      }
      setIsAddingText(false);
    } else if (isAddingRect) {
      const pos = e.target.getStage().getPointerPosition();
      setCurrentRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
    } else if (isBlurring) {
      const pos = e.target.getStage().getPointerPosition();
      setCurrentBlur({ x: pos.x, y: pos.y, width: 0, height: 0 });
    }
  };

  const handlePointerMove = (e) => {
    if (!isDrawing && !isAddingRect && !isBlurring) return;
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    if (!point) return;

    if (isDrawing && currentLine) {
      setCurrentLine((prev) => ({
        ...prev,
        points: [...prev.points, point.x, point.y],
      }));
    } else if (isAddingRect && currentRect) {
      setCurrentRect((prev) => ({
        ...prev,
        width: point.x - prev.x,
        height: point.y - prev.y,
      }));
    } else if (isBlurring && currentBlur) {
      setCurrentBlur((prev) => ({
        ...prev,
        width: point.x - prev.x,
        height: point.y - prev.y,
      }));
    }
  };

  const handlePointerUp = () => {
    if (isDrawing && currentLine) {
      setLines((prev) => [...prev, currentLine]);
      setCurrentLine(null);
    } else if (isAddingRect && currentRect) {
      setRectangles((prev) => [...prev, currentRect]);
      setCurrentRect(null);
    } else if (isBlurring && currentBlur) {
      // Ignore stray clicks that produced no real area.
      if (Math.abs(currentBlur.width) > 2 && Math.abs(currentBlur.height) > 2) {
        setBlurs((prev) => [...prev, currentBlur]);
      }
      setCurrentBlur(null);
    }
  };

  // Clear all annotations
  const handleClearClick = () => {
    setLines([]);
    setTexts([]);
    setRectangles([]);
    setBlurs([]);
    setCurrentLine(null);
    setCurrentRect(null);
    setCurrentBlur(null);
    setSelectedShape(null);
  };

  // Konva exports the stage at display size x pixelRatio. A fixed 3 was fine on
  // a desktop capture, but on a phone it multiplies an already-retina snapshot
  // (or a 12MP photo out of the camera roll) into a base64 payload big enough
  // to be refused, so never scale past the source image's own resolution.
  const getExportPixelRatio = () => {
    if (!imageBase64 || !stageSize.width) return 1;
    return Math.max(1, Math.min(3, imageBase64.width / stageSize.width));
  };

  // Download annotated image
  const handleDownloadClick = () => {
    if (!stageRef.current) return;
    const dataURL = stageRef.current.toDataURL({
      pixelRatio: getExportPixelRatio(),
    });
    const link = document.createElement("a");
    link.href = dataURL;
    link.download = "feedback-screenshot.png";
    link.click();
  };

  // Re-choose the capture: open the browser's screen/window share picker so a
  // different window (or the whole screen) can replace the default snapshot.
  //
  // Task 24511 - iOS Safari has no getDisplayMedia, so on an iPhone this button
  // used to throw the moment it was pressed and then swallow the error, leaving
  // a control that visibly did nothing. It is now only offered where the picker
  // actually exists; every device also gets "Retake snapshot", which reruns the
  // same html2canvas capture with this modal left out of the shot.
  const [recapturing, setRecapturing] = useState(false);
  const supportsScreenShare = useMemo(() => canShareScreen(), []);

  const handleChooseWindow = async () => {
    setRecapturing(true);
    try {
      const shot = await captureScreenWhenReady();
      if (shot) {
        setImageSrc(shot);
        handleClearClick(); // drop annotations tied to the old image
      }
    } catch (err) {
      console.error("Re-capture failed:", err);
    } finally {
      setRecapturing(false);
    }
  };

  const handleRetakeSnapshot = async () => {
    setRecapturing(true);
    try {
      // The bug-report dialog is sitting on top of the page, so leave THAT ONE
      // out of the clone - what gets captured is the screen the user was looking
      // at when they pressed the bug button. It used to exclude every
      // .app-modal / .MuiDialog-root, which meant a retake also deleted the
      // popup being reported on - the most common thing to want a picture of
      // (Task 24522). Its own backdrop lives inside its dialog root, so it goes
      // with it and no blanket backdrop rule is needed.
      const shot = await captureCurrentWindow({
        ignoreSelectors: [".feedback-gen-dialog"],
      });

      if (shot) {
        setImageSrc(shot);
        handleClearClick(); // drop annotations tied to the old image
      } else {
        toast.error(
          "Could not capture this screen. Take a screenshot on your device and upload it below instead."
        );
      }
    } catch (err) {
      console.error("Retake snapshot failed:", err);
      toast.error("Could not capture this screen.");
    } finally {
      setRecapturing(false);
    }
  };

  // Upload screenshot / photo
  const handleImageUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageSrc(ev.target.result);
      handleClearClick(); // reset drawings when new image is loaded
    };
    reader.readAsDataURL(file);
  };

  // Stage sizing based on container + image aspect
  const updateStageSize = () => {
    if (!containerRef.current || !imageBase64) return;

    const containerWidth = containerRef.current.offsetWidth;
    let containerHeight = containerRef.current.offsetHeight;

    // iOS / modal fallback – when height is 0
    if (!containerHeight || containerHeight < 50) {
      containerHeight = window.innerHeight * 0.6;
    }

    const aspectRatio = imageBase64.width / imageBase64.height;

    let newWidth = containerWidth - PresetOffsetNumber;
    let newHeight = newWidth / aspectRatio;

    if (newHeight > containerHeight - PresetOffsetNumber) {
      newHeight = containerHeight - PresetOffsetNumber;
      newWidth = newHeight * aspectRatio;
    }

    setStageSize({
      width: newWidth,
      height: newHeight,
    });
  };

  useEffect(() => {
    updateStageSize();
    window.addEventListener("resize", updateStageSize);
    return () => window.removeEventListener("resize", updateStageSize);
  }, [imageBase64]);

  // Clear annotations when modal closes
  useEffect(() => {
    if (!modal) {
      handleClearClick();
      setImageSrc("");
    }
  }, [modal]);

  const dispatch = useDispatch();

  const handleSendFeedback = () => {
    if (!feedbackData.Title) {
      toast.error("Title is required");
      return;
    }

    // Screenshot is optional – only attach an image if one has been provided.
    const hasImage = imageBase64 && stageRef.current;

    // Keep the Description box clean for the user; append the auto-captured
    // element details (from the point-and-click picker) only at save time.
    const Description = [feedbackData.Description, elementInfo]
      .map((part) => (part || "").trim())
      .filter(Boolean)
      .join("\n\n");

    const sendData = {
      ...feedbackData,
      Description,
      Url: window.location.href,
      images: hasImage
        ? [
            {
              Title: "",
              MimeType: "image/png",
              ImageBase64: stageRef.current.toDataURL({
                pixelRatio: getExportPixelRatio(),
              }),
            },
          ]
        : [],
    };
    dispatch(sendFeedback([sendData]));
  };

  const { feedbackSuccess, loadingFeedback } = useSelector((state) => ({
    feedbackSuccess: state.FeedbackGen.feedbackSuccess,
    loadingFeedback: state.FeedbackGen.loadingFeedback,
  }));

  // Handle API response
  useEffect(() => {
    if (isEmpty(feedbackSuccess)) return;

    if (feedbackSuccess.status === 200) {
      toast.success(feedbackSuccess.message);
      toggle();
    } else {
      toast.error(feedbackSuccess.message);
    }
    dispatch(sendFeedbackSuccess([]));
  }, [feedbackSuccess, dispatch, toggle]);

  // Map stage coords -> image natural pixels so blur crops line up at any zoom.
  const blurScaleX =
    imageBase64 && stageSize.width ? imageBase64.width / stageSize.width : 1;
  const blurScaleY =
    imageBase64 && stageSize.height ? imageBase64.height / stageSize.height : 1;

  // Canvas area (used in both mobile + desktop)
  const CanvasArea = (
    <div
      ref={containerRef}
      className={`stage-container ${isMobile ? "feedback-canvas-mobile" : ""}`}
    >
      {imageBase64 ? (
        <Stage
          ref={stageRef}
          width={stageSize.width}
          height={stageSize.height}
          // Mouse
          onMouseDown={handlePointerDown}
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          // Touch
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        >
          <Layer>
            <KonvaImage
              image={imageBase64}
              width={stageSize.width}
              height={stageSize.height}
            />

            {lines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke={selectedColor}
                strokeWidth={2}
                tension={0.5}
                lineCap="round"
              />
            ))}

            {currentLine && (
              <Line
                points={currentLine.points}
                stroke="black"
                strokeWidth={2}
                tension={0.5}
                lineCap="round"
              />
            )}

            {texts.map((text, i) => (
              <Text
                key={i}
                text={text.text}
                x={text.x}
                y={text.y}
                fontSize={18}
                fill={text.fill}
                draggable
                onClick={(e) => handleSelectShape(e.target)}
              />
            ))}

            {rectangles.map((rect, i) => (
              <Rect
                key={i}
                x={rect.x}
                y={rect.y}
                width={rect.width}
                height={rect.height}
                fill="rgba(255, 0, 0, 0.1)"
                stroke="rgba(255, 0, 0, 1)"
                strokeWidth={2}
                draggable
                onClick={(e) => handleSelectShape(e.target)}
              />
            ))}

            {currentRect && (
              <Rect
                x={currentRect.x}
                y={currentRect.y}
                width={currentRect.width}
                height={currentRect.height}
                fill="rgba(0,0,0,0.5)"
                stroke="black"
                strokeWidth={2}
              />
            )}

            {blurs.map((blur, i) => (
              <BlurRegion
                key={`blur-${i}`}
                image={imageBase64}
                rect={blur}
                scaleX={blurScaleX}
                scaleY={blurScaleY}
              />
            ))}

            {currentBlur && (
              <Rect
                x={currentBlur.x}
                y={currentBlur.y}
                width={currentBlur.width}
                height={currentBlur.height}
                fill="rgba(255,255,255,0.4)"
                stroke="rgba(0,0,0,0.6)"
                dash={[6, 4]}
                strokeWidth={1}
              />
            )}

            {selectedShape && (
              <Transformer
                ref={transformerRef}
                anchorSize={10}
                borderStroke="blue"
                borderStrokeWidth={2}
                resizeEnabled
                rotationEnabled
              />
            )}
          </Layer>
        </Stage>
      ) : (
        <div className="d-flex align-items-center justify-content-center w-100 h-100">
          <p className="text-muted text-center px-3">
            {isMobile
              ? "No screenshot yet - retake or upload one below to start annotating."
              : "No screenshot yet - retake or upload one on the right to start annotating."}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <AppModal
      open={modal}
      onClose={toggle}
      hideClose
      hideFooter
      dividers={false}
      dialogProps={{
        fullScreen: true,
        // Tagged so a retake can leave out this dialog alone. dialogProps is
        // spread after AppModal's own className, so "app-modal" has to be
        // repeated here or the shared modal styling drops off.
        className: "app-modal feedback-gen-dialog",
      }}
      contentProps={{
        style: {
          padding: 0,
          height: "100vh",
          overflowY: "auto", // allow scrolling on mobile
        },
      }}
    >
        <div
          className={
            isMobile ? "feedback-layout-mobile" : "feedback-layout-desktop"
          }
        >
          {isMobile ? (
            <>
              {/* MOBILE: image at TOP, tools underneath */}
              {CanvasArea}
              <div className="feedback-tools-mobile">
                <FeedbackRightSideTools
                  toggle={toggle}
                  selectedColor={selectedColor}
                  setSelectedColor={setSelectedColor}
                  isDrawing={isDrawing}
                  toggleDrawingMode={toggleDrawingMode}
                  isAddingText={isAddingText}
                  toggleTextMode={toggleTextMode}
                  isAddingRect={isAddingRect}
                  toggleRectMode={toggleRectMode}
                  isBlurring={isBlurring}
                  toggleBlurMode={toggleBlurMode}
                  handleClearClick={handleClearClick}
                  handleDownloadClick={handleDownloadClick}
                  feedbackData={feedbackData}
                  setFeedbackData={setFeedbackData}
                  handleSendFeedback={handleSendFeedback}
                  loadingFeedback={loadingFeedback}
                  onUploadImage={handleImageUpload}
                  onChooseWindow={
                    supportsScreenShare ? handleChooseWindow : null
                  }
                  onRetakeSnapshot={handleRetakeSnapshot}
                  hasImage={!!imageSrc}
                  recapturing={recapturing}
                />
              </div>
            </>
          ) : (
            <>
              {/* DESKTOP: canvas left, tools right */}
              {CanvasArea}
              <FeedbackRightSideTools
                toggle={toggle}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                isDrawing={isDrawing}
                toggleDrawingMode={toggleDrawingMode}
                isAddingText={isAddingText}
                toggleTextMode={toggleTextMode}
                isAddingRect={isAddingRect}
                toggleRectMode={toggleRectMode}
                isBlurring={isBlurring}
                toggleBlurMode={toggleBlurMode}
                handleClearClick={handleClearClick}
                handleDownloadClick={handleDownloadClick}
                feedbackData={feedbackData}
                setFeedbackData={setFeedbackData}
                handleSendFeedback={handleSendFeedback}
                loadingFeedback={loadingFeedback}
                onUploadImage={handleImageUpload}
                onChooseWindow={supportsScreenShare ? handleChooseWindow : null}
                onRetakeSnapshot={handleRetakeSnapshot}
                hasImage={!!imageSrc}
                recapturing={recapturing}
              />
            </>
          )}
        </div>
    </AppModal>
  );
};

export default FeedbackApp;
